import React, { memo, useMemo } from 'react';
import { Link } from 'react-router-dom';
import styled, { CSSObject } from '@emotion/styled';
import { generalIcons } from './icons/materialIcons';
import { getBackgroundTheme, getBorders } from '../style';
import { auth10tixVerification } from '../config/Master';
import { HeartNotificationIcon, StarIcon, OfferNew } from './icons/favoriteIcons';
import { ternaryFunc } from '../utils/helpers';
import Camera from './icons/Camera';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import useMediaBreakpoints from '../hooks/useMediaBreakpoints';
import breakpoints from '../theme/material/breakpoints';
import { CustomStyledProps } from 'style/style';
import VerifiedBadgeTooltip from '../components/common/VerifiedBadgeTooltip';
import { FONT_FAMILIES } from 'theme/material/constants';
import { white } from 'theme/colors';

const CardContainer = styled('div')<{
  cardWidth: number | string;
  cardMargin: number | string;
  isMatched: boolean;
  hideBorder?: boolean;
}>(
  ({ hideBorder }): CSSObject => ({
    position: 'relative',
    borderRadius: '6px',
    overflow: 'hidden',
    border: hideBorder ? 'none' : getBorders().PRIMARY,
  }),
  ({ cardWidth, cardMargin }) => ({
    width: cardWidth,
    margin: cardMargin,
  }),
  ({ isMatched, hideBorder }) =>
    isMatched &&
    !hideBorder && {
      border: '2px solid #E21D4A',
      boxShadow: 'inset 0px 0px 0px 1px rgb(188 32 40)',
    }
);

const PhotoAspectRatio = styled('div')({
  paddingTop: '133%',
  position: 'relative',
});

const AccountDetailsContainer = styled('div')({
  color: 'white',
  position: 'absolute',
  left: '0px',
  bottom: '0px',
  width: '100%',
  zIndex: 3,
});

const AccountDetails = styled('div')(
  {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'bottom',
    padding: '0 0 10px 10px',
    flexWrap: 'wrap',
    width: '75%',
    textOverflow: 'ellipsis',
  },
  ({ detailsPadding }: CustomStyledProps) => ({
    padding: detailsPadding,
  })
);

const UsernameContainer = styled('div')(() => ({
  width: '100%',
  color: white,
  display: 'flex',
  alignItems: 'center',
}));

const Username = styled('div')(
  {
    width: 'auto',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'inline-block',
    fontSize: 16,
    fontFamily: FONT_FAMILIES.secondary,
    letterSpacing: '-0.5px',
  },
  ({ overrideWidth }: CustomStyledProps) => ({
    width: overrideWidth,
  })
);

const UserDetails = styled('div')(() => ({
  fontSize: 10,
  color: getBackgroundTheme().cardDetails,
  width: '85%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

const PhotoCountDetails = styled('div')({
  fontSize: 11,
  position: 'absolute',
  padding: '0 10px 10px 0',
  bottom: '0px',
  right: '0px',
  width: '32px',
});

const PhotoCount = styled('span')(
  (): CSSObject => ({
    color: getBackgroundTheme().cardDetails,
    fontSize: 11,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
  })
);

const PublicPhotoCount = styled('div')({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderBottom: '1px solid red',
});

const PrivatePhotoCount = styled('div')({
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

const Gradient = styled('div')({
  background: 'linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, #000 100%);',
  position: 'absolute',
  width: '100%',
  height: '70px',
  bottom: 0,
  left: 0,
  zIndex: 2,
  [breakpoints.down(639.05)]: {
    height: '58px',
  },
});

const Img = styled('div')(
  {
    width: '100%',
    height: '100%',
    position: 'absolute',
    top: '0px',
    left: '0px',
    zIndex: 1,
    borderRadius: '6px',
  },
  ({ imgUrl }: CustomStyledProps) => ({
    backgroundImage: `url(${imgUrl})`,
    backgroundPosition: 'top',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
  })
);

const OnlineDot = styled('div')({
  height: '8px',
  width: '8px',
  backgroundColor: 'lightgreen',
  borderRadius: '50%',
  display: 'inline-block',
  marginRight: 3,
});

const StyledPhotoCount = styled('span')({
  alignSelf: 'flex-end',
});

const Icon = styled('span')({});

const VerifiedBadge = styled('span')({
  marginLeft: 3,
  zIndex: 2,
  fontSize: 0,
});

const ProfileCardBanner = styled('div')<{ customBackground: string }>(
  {
    position: 'absolute',
    top: 0,
    left: 0,
    padding: '8px 0',
    width: '100%',

    zIndex: 5,
    display: 'flex',
    justifyContent: 'center',
    fontSize: 12,
    fontWeight: 600,
    lineHeight: '15px',
    letterSpacing: '-0.3px',
    opacity: 0.9,
    color: '#fff',
    svg: {
      width: 14,
      height: 14,
      marginTop: 1,
      marginRight: 4,
    },
    [breakpoints.down(639.05)]: {
      fontSize: 12,
      svg: {
        height: 12,
      },
    },
  },
  ({ customBackground }: CustomStyledProps) => ({
    background: `${customBackground ? customBackground : '#E21D4A'}`,
  })
);

interface CardProps {
  imgUrl: string;
  cardWidth: string;
  cardMargin: string;
  online: boolean;
  username: string;
  age: string;
  city: string;
  photoCount: number;
  privatePhotoCount: number;
  hashId: string;
  onClick();
  id: string;
  detailsPadding: string;
  isVerified: boolean;
  favorited?: boolean;
  favoritedMe: boolean;
  page: string;
  offer: any;
  isMatched: boolean;
  enableSlider?: any;
  isSelfieVerified: boolean;
  hideBorder?: boolean;
}

const FavoriteCard = ({
  imgUrl,
  cardWidth,
  cardMargin,
  online,
  username,
  age,
  city,
  photoCount,
  privatePhotoCount,
  hashId,
  onClick,
  id,
  isVerified = false,
  favoritedMe,
  isMatched,
  page,
  offer,
  isSelfieVerified,
  hideBorder,
}: CardProps): JSX.Element => {
  const { isMobile } = useMediaBreakpoints();
  const user = useSelector((state: RootState) => state.profile);

  const renderProfileCardBanner = useMemo(() => {
    if (offer !== undefined && offer.data !== undefined) {
      if (offer.data.status === 'accepted') {
        return (
          <ProfileCardBanner
            data-test-id="fav-banner-offer-accepted"
            customBackground="linear-gradient(93deg, #10CBBB 0%, #2B8FD7 100%)"
          >
            <HeartNotificationIcon /> Offer Accepted!
          </ProfileCardBanner>
        );
      }

      if (offer.data.status === 'pending') {
        if (offer.data.to_account_hash_id === user.hash_id) {
          return (
            <ProfileCardBanner
              data-test-id={offer.data.suggest ? 'fav-banner-suggest-request' : 'fav-banner-offer-request'}
              customBackground="#BF9200"
            >
              {offer.data.suggest ? 'Asked you to Offer!' : 'Sent You an Offer!'}
            </ProfileCardBanner>
          );
        }

        return (
          <ProfileCardBanner data-test-id="fav-banner-pending-offer" customBackground="rgb(191, 146, 0)">
            <OfferNew fill="rgba(255,255,255,1)" /> Pending Offer
          </ProfileCardBanner>
        );
      }
    }

    if (isMatched) {
      return (
        // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
        <ProfileCardBanner data-test-id="fav-banner-mutual-favorite">
          <StarIcon isActive={false} fill="#FFF" /> Mutual Favorite!
        </ProfileCardBanner>
      );
    }

    if (favoritedMe && (page === 'from' || page === 'viewed_me')) {
      return (
        // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
        <ProfileCardBanner data-test-id="fav-banner-favorited-you">
          <StarIcon isActive={false} fill="#FFF" /> Favorited You!
        </ProfileCardBanner>
      );
    }

    return '';
  }, [offer, page, offer?.data?.status, isMatched, favoritedMe]);

  const renderAccountDetails = useMemo(
    () => (
      <Link to={`/profile/${hashId}`}>
        <PhotoAspectRatio>
          <AccountDetailsContainer>
            <AccountDetails style={{ width: '75%' }} className="account-details-container">
              <UsernameContainer>
                <Username
                  data-test-id="data-member-username"
                  overrideWidth={isSelfieVerified && !isVerified ? 'auto' : ternaryFunc(isVerified, 'auto', '100%')}
                  style={{ maxWidth: isMobile ? '70%' : '85%' }}
                >
                  {username}
                </Username>
                {((auth10tixVerification && isVerified) || isSelfieVerified) && (
                  <VerifiedBadge data-test-id="data-verified-badge">
                    <VerifiedBadgeTooltip
                      showToolTip={true}
                      isIdVerified={isVerified}
                      isSelfieVerified={isSelfieVerified}
                      customVerifiedBadgeStyle={{ top: 0, [breakpoints.down(639.05)]: { top: 0 } }}
                    />
                  </VerifiedBadge>
                )}
              </UsernameContainer>
              <UserDetails data-test-id="data-member-age-city">
                {online ? <OnlineDot /> : ''} {age} &middot; {city}
              </UserDetails>
            </AccountDetails>
            <PhotoCountDetails data-test-id="data-member-photo-count">
              <PhotoCount>
                <PublicPhotoCount>
                  <Icon data-test-id="data-camera-icon">
                    <Camera />
                  </Icon>
                  <StyledPhotoCount>{photoCount}</StyledPhotoCount>
                </PublicPhotoCount>
                <PrivatePhotoCount>
                  <Icon data-test-id="data-eye-hidden-icon">
                    <generalIcons.eye.hidden />
                  </Icon>
                  <StyledPhotoCount>{privatePhotoCount}</StyledPhotoCount>
                </PrivatePhotoCount>
              </PhotoCount>
            </PhotoCountDetails>
          </AccountDetailsContainer>
          <Gradient />
          <Img className="data-avatar-image-url" imgUrl={imgUrl} />
        </PhotoAspectRatio>
      </Link>
    ),
    [
      hashId,
      isMobile,
      isVerified,
      imgUrl,
      privatePhotoCount,
      photoCount,
      online,
      age,
      city,
      username,
      auth10tixVerification,
    ]
  );

  return (
    <CardContainer
      id={id}
      className="card-items"
      cardWidth={cardWidth}
      cardMargin={cardMargin}
      isMatched={isMatched}
      hideBorder={hideBorder}
      onClick={onClick}
    >
      {renderProfileCardBanner}
      {renderAccountDetails}
    </CardContainer>
  );
};

FavoriteCard.defaultProps = {
  cardWidth: '300px',
  id: '',
};

export default memo(FavoriteCard);
